import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import ReactSelect, { formatDotStyles } from '../ReactSelect';
import { format, parseISO, subMonths } from 'date-fns';
import useMode from '../../hooks/useMode';
import { PortalWithState } from 'react-portal';
import { DropdownIndicatorProps } from 'react-select';
import ReactSelectButton from '../ReactSelect/ReactSelectButton';
import GaugeEditModal from './GaugeEditModal';

export const GET_GAUGES_COMPANY = gql`
  query GetGauges($where: JSON, $orderBy: [ColumnOrder]) {
    vendors(where: $where, orderBy: $orderBy, limit: null) {
      id
      profileId
      profile(required: true) {
        id
        backflowTester {
          gauges(required: true, limit: null) {
            id
            gaugeNumber
            calibrationDate
            isDefault
            archived
          }
        }
      }
    }
  }
`;

export const GET_GAUGES_INDIVIDUAL = gql`
  query GetGauges {
    me {
      id
      backflowTester {
        gauges(required: true, limit: null) {
          id
          gaugeNumber
          calibrationDate
          isDefault
          archived
        }
      }
    }
  }
`;

type VendorResponse = {
  vendors: { profile: { backflowTester: { gauges: Gauge[] } } }[];
  me: undefined;
};

type MeResponse = {
  me: { backflowTester: { gauges: Gauge[] } };
  vendors: undefined;
};

export type Gauge = {
  id: string;
  gaugeNumber: string;
  calibrationDate: string;
  isDefault: boolean;
  archived?: number | null;
};

type GaugePickerPickerProps = {
  testerProfileId: string;
  gaugeId?: string | number;
  onChange: (gauge: Gauge | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
};

const GaugePicker = ({
  testerProfileId,
  gaugeId,
  onChange,
  placeholder,
  allowCreate,
}: GaugePickerPickerProps): JSX.Element => {
  const mode = useMode();

  const { error, data, refetch } = useQuery<VendorResponse | MeResponse>(
    mode === 'individual' ? GET_GAUGES_INDIVIDUAL : GET_GAUGES_COMPANY,
    {
      fetchPolicy: 'network-only',
      variables: {
        where:
          mode === 'individual'
            ? undefined
            : {
                profileId: testerProfileId,
              },
        orderBy:
          mode === 'individual'
            ? undefined
            : // [
              //   {
              //     col: 'calibrationDate',
              //     dir: 'DESC',
              //   },
              // ]
              [
                {
                  col: 'profile.backflowTester.gauges.calibrationDate',
                  dir: 'DESC',
                },
              ],
        limit: null,
      },
    }
  );

  const gauges = data?.me
    ? data?.me?.backflowTester?.gauges
    : (data?.vendors[0]?.profile?.backflowTester?.gauges ?? []);

  const selectedGauge: Gauge | undefined = useMemo(
    () => gauges.find((gauge) => gauge.id == gaugeId),
    [gauges, gaugeId]
  );

  const availableGauges: Gauge[] = useMemo(() => {
    if (gauges.length === 0) {
      return [];
    }
    return gauges.filter(
      (gauge) =>
        !gauge.archived &&
        subMonths(Date.now(), 18).getTime() <
          parseISO(gauge.calibrationDate).getTime()
    );
  }, [gauges]);

  useEffect(() => {
    let gauge: Gauge | undefined = selectedGauge;

    if (gaugeId && gauges.length > 0 && !selectedGauge) {
      console.warn('Clearing not found gauge');
      gauge = undefined;
    }
    if (availableGauges.length > 0 && !gauge) {
      const defaultGauge = availableGauges.find((gauge) => gauge.isDefault);
      if (defaultGauge) {
        console.log('No gauge selected. Using default gauge');
        gauge = defaultGauge;
      } else {
        console.log('No gauge selected. Using first in list');
        gauge = availableGauges[0];
      }
      if (gauge?.id !== (gaugeId || undefined)) {
        // can't call setState for parent component in render
        setTimeout(() => {
          onChange(gauge ?? null);
        });
      }
    }
  }, [gaugeId, gauges, availableGauges, selectedGauge]);

  if (selectedGauge && !availableGauges.includes(selectedGauge)) {
    availableGauges.push(selectedGauge);
  }

  const assignGauge = async (gauge: Gauge) => {
    await refetch();
    onChange(gauge);
  };

  return (
    <PortalWithState closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => {
        return (
          <>
            <ReactSelect
              value={selectedGauge}
              options={availableGauges}
              noOptionsMessage={() => null}
              menuPlacement="auto"
              components={{
                DropdownIndicator: (
                  props: PropsWithChildren<DropdownIndicatorProps<Gauge, false>>
                ) => {
                  return allowCreate ? (
                    <ReactSelectButton
                      onClick={openPortal}
                      title="Add new gauge"
                    />
                  ) : null;
                },
              }}
              styles={{
                menu: (base) => ({
                  ...base,
                  minWidth: '250px',
                }),
              }}
              onChange={(selected: Gauge | null) => {
                if (!selected) {
                  onChange(null);
                } else {
                  onChange(selected);
                }
              }}
              placeholder={placeholder ?? 'Select gauge...'}
              getOptionLabel={(gauge: Gauge) =>
                `${gauge.gaugeNumber} (${format(
                  parseISO(gauge.calibrationDate),
                  'M/d/yyyy'
                )})`
              }
              getOptionValue={(gauge: Gauge) => gauge.id}
            />
            {portal(
              <GaugeEditModal
                action="create"
                gauge={{
                  profileId: testerProfileId,
                }}
                onClose={closePortal}
                assignGauge={assignGauge}
              />
            )}
          </>
        );
      }}
    </PortalWithState>
  );
};

export default GaugePicker;
